import React from "react";
import { cn } from "./cn";
import { ABUNDO_MARK_URL } from './brandAssets';

/**
 * Bun icon rail: the 60px advisor navigation. Spec: CRM Dashboard v2 /
 * Pipeline canvases (identical rails).
 *
 *   <IconRail footer={<Avatar initials="AR" />}>
 *     <IconRail.Item label="Today" active><HomeIcon className="w-[18px] h-[18px]" /></IconRail.Item>
 *     <IconRail.Item label="Inbox" dot><MailIcon className="w-[18px] h-[18px]" /></IconRail.Item>
 *     <IconRail.Spacer />
 *     <IconRail.Item label="Settings"><CogIcon className="w-[18px] h-[18px]" /></IconRail.Item>
 *   </IconRail>
 *
 * `logo` defaults to the Abundo mark; pass a node to replace it. Icons are
 * 18px heroicons outline, passed as children so the rail stays icon-agnostic.
 */
export default function IconRail({ logo, footer, className, children }) {
  return (
    <aside
      className={cn(
        "sticky top-0 h-screen box-border w-[60px] shrink-0 bg-bun-canvas border-r border-bun-line",
        "flex flex-col items-center py-[14px] gap-1 font-instrument",
        className
      )}
    >
      <div className="mb-[18px]">
        {logo || (
          <img src={ABUNDO_MARK_URL} alt="Abundo" className="w-8 h-8 object-contain" />
        )}
      </div>
      {children}
      {footer && <div className="mt-1">{footer}</div>}
    </aside>
  );
}

function Item({ label, active, dot, onClick, className, children }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      className={cn(
        "relative w-[38px] h-[38px] rounded-bun border-0 bg-transparent cursor-pointer",
        "flex items-center justify-center shrink-0",
        "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue",
        active
          ? "bg-bun-ink text-bun-on-ink"
          : "text-bun-stone hover:bg-bun-mist-deep hover:text-bun-text",
        className
      )}
    >
      {children}
      {dot && (
        <span className="absolute top-[6px] right-[6px] w-[7px] h-[7px] rounded-full bg-bun-blue border-[1.5px] border-bun-canvas" />
      )}
    </button>
  );
}

function Spacer() {
  return <div className="flex-1" />;
}

IconRail.Item = Item;
IconRail.Spacer = Spacer;
